import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useContext, useEffect, useState } from 'react';
import { Container, Row } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ModalBody from 'react-bootstrap/ModalBody';
import Button from '../../../components/Button';
import CalloutCard from '../../../components/CalloutCard';
import DefaultCard from '../../../components/DefaultCard';
import IconButton from '../../../components/IconButton/IconButton';
import Link from '../../../components/Link';
import ModalHeader from '../../../components/ModalHeader';
import PricingCalculatorCard, {
  PricingCardListItem,
} from '../../../components/PricingCalculatorCard';
import { RadioGroup } from '../../../components/Radio';
import SelectableCard from '../../../components/SelectableCard';
import { EnrollmentStore } from '../../../providers/enrollment';
import { getAllHealthCare } from '../../../services/enrollment';
import { openExternalLink } from '../../../utils/utils';

export type customHealthFsaModalProps = {
  showHealthFSAModal: boolean;
  handleCloseHealthFSAModal: () => void;
  fixedContribution?: number;
};

const buttonForCalloutCard = [
  <Button
    variant="secondary-outline"
    rightIcon={light('calculator')}
    className="callout-button text-nowrap"
    size="sm"
    onClick={() =>
      openExternalLink('https://btr.lifeatworkportal.com/dst/fsa/default.aspx')
    }
  >
    FSA Needs Calculator
  </Button>,
];

const HealthFsaModal = ({
  showHealthFSAModal,
  handleCloseHealthFSAModal,
  fixedContribution,
}: customHealthFsaModalProps) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [healthFSAData, setHealthFSAData] = React.useState<any>({});
  const { activeSpendingAccounts, updateCost, updatePlanName } =
    useContext(EnrollmentStore);

  const [calculatorValue, setcalculatorValue] = React.useState<string | number>(
    '0'
  );

  const [tellMeMore, setTellMeMore] = useState(false);

  const [listItems, setListItems] = React.useState<Array<PricingCardListItem>>([
    {
      text: '',
      value: 400,
    },
  ]);

  useEffect(() => {
    const data = getAllHealthCare();

    if (data) {
      setHealthFSAData(data);
      setListItems([
        {
          text: '',
          value: fixedContribution
            ? fixedContribution
            : data['plans'][0].selfAnnualContributions,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    const currentBenefit = activeSpendingAccounts.find(
      (benefit: any) => benefit.type === 'health-care'
    );
    const index = healthFSAData.plans?.findIndex(
      (plan: any) => plan.id === currentBenefit?.plan?.id
    );
    setcalculatorValue(index?.toString());
  }, [activeSpendingAccounts, healthFSAData]);

  const handleInputValueChange = (
    value: number | undefined | null,
    listItems: Array<PricingCardListItem>,
    setListItems: Function
  ) => {
    let listItemsNew = listItems;
    if (listItemsNew[0]) {
      listItemsNew[0].value = value;
    } else {
      listItemsNew[0] = {
        text: '',
        value: value,
      };
    }
    setListItems(listItemsNew);
  };

  const handleCalculatorChange = (e: string | number) => {
    setcalculatorValue(e.toString());
  };

  const handleChangeListItems = (value: number | undefined) => {
    handleInputValueChange(value, listItems, setListItems);
  };

  const handleModalClose = () => {
    handleCloseHealthFSAModal();
    setcalculatorValue('0');
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const handleTellMeMore = () => {
    setTellMeMore((prev) => !prev);
  };

  return (
    <Modal
      show={showHealthFSAModal}
      onHide={handleCloseHealthFSAModal}
      dialogClassName="modal-xl"
    >
      <ModalHeader
        rightButton={
          <div onClick={toggleDetails} className={'icon-button-wrapper'}>
            <IconButton
              variant="icon-default"
              icon={light('circle-exclamation')}
              extLabel="Benefit Info"
            />
          </div>
        }
        leftIcon={light('bed-pulse')}
        leftIconVariant={'icon-default'}
      >
        <h4 className="text-sm-20-lg-32">
          {healthFSAData['generic'] ? healthFSAData['generic']['title'] : ''}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Row className={`expandable-wrapper ${showDetails ? 'open' : ''}`}>
          <div className="d-flex justify-content-center">
            <DefaultCard
              closeButton={
                <Link 
                  className='pt-0' 
                  isAlignAtEnd 
                  isWhiteIcon 
                  rightIcon={light('close')}
                  onClick={toggleDetails}
                />                      
              }
              variant="blue-motif"
              leftHeading={
                healthFSAData['generic']
                  ? healthFSAData['generic']['info']['title']
                  : ''
              }
              leftSubheading={
                <Container className={'px-0 d-flex flex-column'}>
                  {healthFSAData['generic'] ? (
                    <>
                      <span>
                        {healthFSAData['generic']['info']['description']}
                      </span>
                      {tellMeMore && (
                        <span className={'mt-1'}>
                          {
                            healthFSAData['generic']['info'][
                              'additionalDescription'
                            ]
                          }
                        </span>
                      )}
                    </>
                  ) : (
                    ''
                  )}
                </Container>
              }
              button={
                <Button
                  onClick={handleTellMeMore}
                  variant="primary-shadow"
                  size="sm"
                >
                  {tellMeMore ? 'Tell me less' : 'Tell me more'}
                </Button>
              }
              className={'mb-12'}
            />
          </div>
        </Row>
        <Row className="pb-7 pb-lg-12">
          <Container className="d-flex justify-content-center">
            <CalloutCard
              cardIcon={light('screwdriver-wrench')}
              buttons={buttonForCalloutCard}
            >
              <Row>
                <span className="bolder-text text-ligther-grey">
                  Need help knowing how to contribute?
                </span>
                <p>
                  Use the{' '}
                  <span className="bolder-text text-ligther-grey">
                    FSA Calculator{' '}
                  </span>
                  your needs
                </p>
              </Row>
            </CalloutCard>
          </Container>
        </Row>
        <Row>
          <RadioGroup
            name={'fsa-radio-grooup'}
            value={calculatorValue}
            onChange={handleCalculatorChange}
          >
            <PricingCalculatorCard
              title="How much will you like to contribute?"
              subtitle={
                <div>
                  <b>Annual Contribution</b> Range $120 - $5,000
                </div>
              }
              radioButtonValue={'0'}
              disableNumberInputField={fixedContribution ? true : false}
              listItems={listItems}
              handleChange={handleChangeListItems}
              selected={calculatorValue === '0'}
              radioButtonDefaultChecked={calculatorValue === '0'}
            />
            <div className="px-4 px-lg-5 mt-3">
              <SelectableCard
                title="No Coverage"
                prePaydPeriod={0}
                radioButtonValue={'1'}
                selected={calculatorValue === '1'}
                disabled
              />
            </div>
          </RadioGroup>
        </Row>
      </ModalBody>
      <Modal.Footer className="pt-7">
        <Button
          onMouseDown={handleModalClose}
          className="text-nowrap mt-7 mt-lg-0"
          variant="primary-outline"
        >
          Cancel
        </Button>
        <Button
          onMouseDown={() => {
            const amount = listItems.reduce((acc, curr: any) => {
              acc += parseInt(curr.value);
              return acc;
            }, 0);

            updateCost(
              'health-care',
              calculatorValue === '0'
                ? { ...healthFSAData.plans[calculatorValue], cost: amount / 12 }
                : healthFSAData.plans[calculatorValue]
            );

            updatePlanName('health-care', healthFSAData.plans[calculatorValue]);
            handleCloseHealthFSAModal();
          }}
          className="text-nowrap"
          variant="primary"
        >
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HealthFsaModal;
